import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, Settings, LogOut, User, ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI } from '../../lib/api';

const TopBar = ({ onToggleSidebar, onMobileMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await notificationAPI.getAll({ unreadOnly: true, limit: 1 });
        setUnreadCount(res.data.unreadCount || 0);
      } catch {}
    };
    if (user) fetchUnread();
    const interval = setInterval(() => { if (user) fetchUnread(); }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <header style={{
      position: 'fixed', top: 0, right: 0, left: 'var(--sidebar-width)',
      height: 64, background: '#fff', borderBottom: '1px solid #E5E7EB',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 24px', zIndex: 100, transition: 'left 0.3s ease'
    }}>
      {/* Left */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-ghost btn-icon" onClick={onToggleSidebar} style={{ display: 'none' }}
          id="desktop-toggle">
          <Menu size={20} />
        </button>
        <button className="btn btn-ghost btn-icon" onClick={onMobileMenuClick}
          style={{ display: 'none' }} id="mobile-toggle">
          <Menu size={20} />
        </button>
        <button className="btn btn-ghost btn-icon" onClick={onToggleSidebar}>
          <Menu size={20} />
        </button>
      </div>

      {/* Right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Notifications */}
        <button
          className="btn btn-ghost btn-icon"
          onClick={() => navigate('/notifications')}
          style={{ position: 'relative' }}
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 4, width: 18, height: 18,
              background: '#EF4444', borderRadius: '50%', fontSize: 10,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, border: '2px solid #fff'
            }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* User Dropdown */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDropdownOpen(p => !p)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: '#fff', cursor: 'pointer', fontSize: 14
            }}
          >
            <div className="avatar avatar-sm">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <span style={{ fontWeight: 500, color: '#374151', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.firstName}
            </span>
            <ChevronDown size={14} color="#9CA3AF" />
          </button>

          {dropdownOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
              boxShadow: '0 10px 30px rgba(0,0,0,0.12)', minWidth: 200, zIndex: 1000,
              overflow: 'hidden'
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{user?.email}</div>
              </div>
              <div style={{ padding: 6 }}>
                {[
                  { icon: User, label: 'Profile', action: () => navigate('/profile') },
                  { icon: Settings, label: 'Settings', action: () => navigate('/settings') },
                  { icon: LogOut, label: 'Sign Out', action: handleLogout, danger: true }
                ].map(({ icon: Icon, label, action, danger }) => (
                  <button key={label} onClick={() => { action(); setDropdownOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '9px 12px', borderRadius: 7,
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontSize: 14, color: danger ? '#EF4444' : '#374151',
                      fontWeight: 400, textAlign: 'left'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = danger ? '#FEF2F2' : '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
